import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Button,
  Table,
  TableContainer,
  TableProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import _ from 'lodash';
import { sortBy } from 'lodash';
import {
  ChangeEvent,
  ReactNode,
  Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

type OmitTableProps = 'onChange';

export interface DataTable<R> extends Omit<TableProps, OmitTableProps> {
  columnFactory: (
    cb: <C extends keyof R>(
      c: C,
      dc: Omit<DataColumn<R, R[C]>, 'key'>
    ) => DataColumn<R, any>
  ) => DataColumn<R, any>[];
  value: R[] | undefined;
  onChange: (rows: R[], changed: number[]) => void;
  onSortChange?: (columnKey: keyof R, direction: 'asc' | 'desc') => void;
}

export interface DataColumn<R, C> {
  key: keyof R;
  sortValue?: (v: C, r: R) => any;
  header: ReactNode;
  render: (v: C, r: R) => ReactNode;
  renderEdit?: (m: {
    value: C;
    ref: Ref<any>;
    onClose: () => void;
    onChange: React.ChangeEventHandler;
    onValueChange: (v: C | undefined, close?: boolean) => void;
  }) => ReactNode;
  selectTextOnClick?: boolean;
}

interface ColumnSort<R> {
  key: keyof R;
  direction: 'asc' | 'desc';
}

interface Coordinates {
  y: number;
  x: number;
}

export const DataTable = <R extends any>(props: DataTable<R>) => {
  const { columnFactory, value = [], onChange, onSortChange, ...rest } = props;
  const [sortColumn, setSortColumn] = useState<ColumnSort<R> | undefined>(
    undefined
  );
  const [editingCell, setEditingCell] = useState<Coordinates | undefined>(
    undefined
  );
  const [editingValue, setEditingValue] = useState<any>(undefined);

  const columns = useMemo(
    () => columnFactory((key, dc) => ({ key, ...dc })),
    [columnFactory]
  );

  const sortedItems = useMemo(() => {
    const mappedValues = value.map((r, x) => Object.assign({}, r, { x }));
    if (!sortColumn) {
      return mappedValues;
    }

    const columnSettings = columns.find((c) => c.key === sortColumn.key);
    if (!columnSettings) {
      return mappedValues;
    }

    let results = mappedValues.map((row, ind) => {
      let rowValue =
        columnSettings.sortValue?.(row[sortColumn.key], row) ??
        row[sortColumn.key];

      if (typeof rowValue === 'string') {
        rowValue = rowValue.toLowerCase();
      }

      return {
        sortValue: rowValue,
        row,
      };
    });

    results = sortBy(results, 'sortValue');
    if (sortColumn.direction === 'desc') {
      results = results.reverse();
    }

    return results.map((r) => r.row);
  }, [sortColumn, value, columns]);

  const editingCellContainerRef = useRef<HTMLDivElement>(null);
  const editingCellBodyRef = useRef<HTMLElement>(null);
  const commit = useCallback((v: any) => {
    if (!editingCell) return undefined;
    const { x, y } = editingCell;
    const c = columns[y];
    const newValue = [...value];
    (newValue[x] as any)[c.key] = v;
    onChange(newValue, [x]);
    setEditingValue(undefined);
    setEditingCell(undefined);
  }, [editingCell, columns, value, onChange]);

  const beginEditingCell = useCallback((xy: Coordinates, e: Event) => {
    const { x, y } = xy;
    const c = columns[y];
    const { selectTextOnClick: selectAllText = true } = c;

    if (!c.renderEdit) return;
    e.preventDefault();

    let editValue = (value[x] as any)[c.key];

    if (e.type === 'keydown') {
      editValue = (e as KeyboardEvent).key as any;
    }

    setEditingCell(xy);
    setEditingValue(editValue);

    setTimeout(() => {
      const target = editingCellBodyRef.current;
      if (!target) {
        return;
      }

      target.focus();

      if (
        e.type === 'click' &&
        selectAllText &&
        target instanceof HTMLInputElement
      ) {
        target.setSelectionRange(0, target.value.length);
      }
    }, 20);
  }, [columns, value]);

  const handleCellClick = useCallback((xy: Coordinates) => (e: React.MouseEvent) => {
    const { x, y } = xy;
    const c = columns[y];
    if (editingCellContainerRef.current?.contains(e.currentTarget)) {
      return;
    }
    beginEditingCell(xy, e as any);
  }, [columns, beginEditingCell]);

  const handleCellKeyDown = useCallback((xy: Coordinates) => (e: React.KeyboardEvent) => {
    if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return undefined;

    const c = columns[xy.y];
    const { x, y } = xy;

    if (editingCell?.x === x && editingCell?.y === y) {
      if (_.some(['Escape', 'Tab', 'Enter'], (s) => s === e.key)) {
        commit(editingValue);
        return;
      }

      return;
    }

    if (e.key !== 'Tab') {
      beginEditingCell(xy, e as any);
      return;
    }
  }, [columns, editingCell, editingValue, commit, beginEditingCell]);

  const editCell = useMemo(() => {
    if (!editingCell) return undefined;
    const { y: eY } = editingCell;
    const c = columns[eY];

    if (!c.renderEdit) return undefined;

    return c.renderEdit({
      value: editingValue,
      ref: editingCellBodyRef,
      onClose: () => commit(editingValue),
      onChange: (e: ChangeEvent<any>) => setEditingValue(e.target.value as any),
      onValueChange: (v, c = false) => {
        if (c) {
          commit(v);
        } else {
          setEditingValue(v);
        }
      },
    });
  }, [editingValue, editingCell, commit, columns]);

  const rows = useMemo(() => {
    return sortedItems.map((row) => {
      const { x } = row;
      return (
        <Tr key={x}>
          {columns.map((c, y) => {
            const cellValue = (row as any)[c.key];

            return (
              <Td
                key={y}
                tabIndex={0}
                onClick={handleCellClick({ x, y })}
                onKeyDown={handleCellKeyDown({ x, y })}
              >
                {editingCell?.x !== x || editingCell?.y !== y
                  ? c.render(cellValue, row)
                  : editCell}
              </Td>
            );
          })}
        </Tr>
      );
    });
  }, [sortedItems, columns, handleCellClick, handleCellKeyDown, editingCell?.x, editingCell?.y, editCell]);

  return (
    <TableContainer>
      <Table {...rest}>
        <Thead tabIndex={-1}>
          <Tr>
            {columns.map((c, ind) => {
              const { key, header } = c;
              return (
                <Th key={ind} tabIndex={-1}>
                  <Button
                    width="100%"
                    fontSize="xs"
                    variant="link"
                    rightIcon={
                      (sortColumn?.key === c.key && (
                        <ColumnSortIcon direction={sortColumn?.direction} />
                      )) ||
                      undefined
                    }
                    onClick={() => {
                      if (
                        sortColumn?.key === c.key &&
                        sortColumn?.direction === 'asc'
                      ) {
                        setSortColumn({ key: c.key as any, direction: 'desc' });
                        return;
                      }
                      setSortColumn({ key: c.key as any, direction: 'asc' });
                    }}
                  >
                    {header}
                  </Button>
                </Th>
              );
            })}
          </Tr>
        </Thead>
        <Tbody>{rows}</Tbody>
      </Table>
    </TableContainer>
  );
};

function ColumnSortIcon<R>(props: { direction: 'asc' | 'desc' | undefined }) {
  switch (props?.direction) {
    case 'asc':
      return <ChevronUpIcon />;
    case 'desc':
      return <ChevronDownIcon />;
    default:
      return <></>;
  }
}
